import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ThreeScene from "../components/ThreeScene";
import { PHONE, WHATSAPP_URL, CITY, services, stats, testimonials } from "../lib/data";

const iconMap = {
    gem: "fa-gem",
    stairs: "fa-stairs",
    building: "fa-building",
    shield: "fa-shield-halved",
    sparkles: "fa-wand-sparkles",
    wrench: "fa-screwdriver-wrench",
};

export default function Home() {
    return (
        <main data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-screen dark-marble-bg overflow-hidden grain">
                <div className="absolute inset-0 pointer-events-none z-0 opacity-40 lg:hidden">
                    <ThreeScene variant="hero" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 pb-24 min-h-screen flex items-center">
                    <div className="grid lg:grid-cols-2 gap-10 items-center w-full">
                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            className="text-right relative z-10"
                        >
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                className="eyebrow mb-6"
                            >
                                <i className="fa-solid fa-crown ml-2" />فخامة تلمسها بعينك
                            </motion.div>
                            <h1 className="hero-title text-white mb-6">
                                نمنح الرخام <br />
                                <span className="gold-text">بريقاً ذهبياً</span>
                                <br /> يفوق الجديد
                            </h1>
                            <p className="text-white/75 text-lg md:text-xl leading-relaxed mb-10 max-w-lg mr-0">
                                متخصصون في جلي وتلميع الرخام بـ{CITY} - كريستال، درج، واجهات، عزل وأرضيات بأعلى معايير الحرفية الأوروبية.
                            </p>
                            <div className="flex flex-wrap gap-4 mb-12">
                                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="gold-btn" data-testid="hero-whatsapp-btn">
                                    <i className="fa-brands fa-whatsapp ml-2" />احجز عبر واتساب
                                </a>
                                <a href={`tel:${PHONE}`} className="outline-btn" data-testid="hero-call-btn">
                                    <i className="fa-solid fa-phone ml-2" />
                                    <span dir="ltr">{PHONE}</span>
                                </a>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-[rgba(201,169,97,0.2)]">
                                {stats.map((s, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.6 + i * 0.1 }}
                                        data-testid={`stat-${i}`}
                                    >
                                        <div className="gold-text font-display text-3xl md:text-4xl mb-1">{s.value}</div>
                                        <div className="text-white/60 text-xs">{s.label}</div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        <div className="hidden lg:block h-[600px] relative">
                            <ThreeScene variant="hero" />
                        </div>
                    </div>
                </div>

                {/* scroll cue */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#c9a961] text-xs flex flex-col items-center gap-2 z-10">
                    <span>اسحب للأسفل</span>
                    <motion.i
                        className="fa-solid fa-chevron-down"
                        animate={{ y: [0, 8, 0] }}
                        transition={{ repeat: Infinity, duration: 1.6 }}
                    />
                </div>
            </section>

            {/* MARQUEE */}
            <section className="bg-[#0a0a0a] border-y border-[rgba(201,169,97,0.2)] py-6 overflow-hidden">
                <div className="marquee-track">
                    {[...Array(2)].map((_, k) => (
                        <div key={k} className="flex items-center gap-16 px-8 shrink-0">
                            {["كريستال", "◆", "درج الرخام", "◆", "واجهات الحجر", "◆", "عزل النانو", "◆", "أرضيات لامعة", "◆", "ترميم الرخام", "◆", "خبرة 12 سنة", "◆"].map((t, i) => (
                                <span key={i} className={`font-display text-2xl md:text-3xl ${t === "◆" ? "text-[#c9a961]" : "text-white/90"}`}>
                                    {t}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICES */}
            <section className="marble-bg py-24 md:py-32 relative">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="mb-16 max-w-2xl">
                        <div className="eyebrow mb-4">خدماتنا</div>
                        <h2 className="section-title text-[#0a0a0a] mb-5">
                            حرفية تصنع <span className="gold-text">فارقاً</span>
                        </h2>
                        <div className="divider-gold mb-6" />
                        <p className="text-[#0a0a0a]/70 text-lg">
                            نقدم منظومة متكاملة من خدمات الرخام بمعدات ألمانية وفريق فني معتمد.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((s, i) => (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="card-3d h-[440px] relative"
                                data-testid={`service-card-${s.id}`}
                            >
                                <div className="card-3d-inner relative w-full h-full">
                                    {/* FRONT */}
                                    <div className="card-face bg-white gold-ring">
                                        <div className="relative h-56 overflow-hidden">
                                            <img src={s.image} alt={s.title} className="w-full h-full object-cover gallery-img" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                            <div className="absolute top-4 right-4 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #e8c86a, #8a6d2b)" }}>
                                                <i className={`fa-solid ${iconMap[s.icon]} text-[#0a0a0a]`} />
                                            </div>
                                        </div>
                                        <div className="p-6 text-right">
                                            <h3 className="font-display text-2xl text-[#0a0a0a] mb-2">{s.title}</h3>
                                            <p className="text-[#0a0a0a]/65 text-sm">{s.short}</p>
                                            <div className="mt-4 text-[#c9a961] text-xs font-semibold flex items-center gap-2 justify-end">
                                                مرر لمعرفة المزيد <i className="fa-solid fa-arrow-left" />
                                            </div>
                                        </div>
                                    </div>
                                    {/* BACK */}
                                    <div className="card-face card-face-back p-8 text-right flex flex-col justify-between" style={{ background: "linear-gradient(160deg, #14120a 0%, #0a0a0a 100%)" }}>
                                        <div>
                                            <h3 className="font-display text-2xl text-[#e8c86a] mb-3">{s.title}</h3>
                                            <p className="text-white/75 text-sm leading-relaxed mb-4">{s.desc}</p>
                                            <ul className="space-y-2">
                                                {s.features.map((f, k) => (
                                                    <li key={k} className="text-white/80 text-xs flex items-center gap-2 justify-end">
                                                        {f} <i className="fa-solid fa-check text-[#c9a961]" />
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="gold-btn text-sm text-center" style={{ padding: "0.6rem 1.2rem" }}>
                                            <i className="fa-brands fa-whatsapp ml-2" />اطلب الخدمة
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="dark-marble-bg py-24 md:py-32 relative overflow-hidden grain">
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="relative h-[500px] hidden lg:block">
                            <ThreeScene variant="side" />
                        </div>
                        <div>
                            <div className="eyebrow mb-4">آلية العمل</div>
                            <h2 className="section-title text-white mb-5">
                                من <span className="gold-text">المعاينة</span> إلى اللمعان
                            </h2>
                            <div className="divider-gold mb-8" />
                            <div className="space-y-6">
                                {[
                                    { n: "01", t: "المعاينة المجانية", d: "زيارة الموقع وتقدير حالة الرخام والاحتياج بدقة" },
                                    { n: "02", t: "عرض السعر الشفاف", d: "تفصيل كامل للتكاليف والمراحل دون رسوم مخفية" },
                                    { n: "03", t: "التنفيذ الحرفي", d: "فريق فني معتمد وماكينات ألمانية للنتيجة المثالية" },
                                    { n: "04", t: "التسليم والضمان", d: "تسليم نظيف مع ضمان يصل إلى 5 سنوات" },
                                ].map((step, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, x: 30 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.12 }}
                                        className="flex gap-5 items-start"
                                        data-testid={`process-step-${i}`}
                                    >
                                        <div className="font-latin text-4xl md:text-5xl gold-text shrink-0 w-16">{step.n}</div>
                                        <div>
                                            <h4 className="font-display text-xl text-white mb-1">{step.t}</h4>
                                            <p className="text-white/65 text-sm">{step.d}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* GALLERY */}
            <section className="marble-bg py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="mb-14 text-center">
                        <div className="eyebrow mb-4">أعمالنا</div>
                        <h2 className="section-title text-[#0a0a0a] mb-4">
                            نتائج <span className="gold-text">تتحدث عن نفسها</span>
                        </h2>
                        <div className="divider-gold mx-auto" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {[
                            "https://images.unsplash.com/photo-1618221118493-9cfa1a1c00da?w=800&q=80",
                            "https://images.unsplash.com/photo-1615873968403-89e068629265?w=800&q=80",
                            "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80",
                            "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800&q=80",
                            "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80",
                            "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=80",
                            "https://images.unsplash.com/photo-1615529162924-f8605388461d?w=800&q=80",
                            "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80",
                        ].map((src, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.06 }}
                                className={`relative overflow-hidden rounded-2xl gold-ring ${i % 3 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}
                                data-testid={`gallery-item-${i}`}
                            >
                                <img src={src} alt={`gallery-${i}`} className="w-full h-full object-cover gallery-img" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity" />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="bg-[#0a0a0a] py-24 md:py-32 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #c9a961, transparent 60%)" }} />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <div className="mb-14 text-center">
                        <div className="eyebrow mb-4">آراء العملاء</div>
                        <h2 className="section-title text-white">
                            ثقة <span className="gold-text">تُبنى بحرفية</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((t, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.15 }}
                                className="glass-dark rounded-2xl p-8 tilt-hover"
                                data-testid={`testimonial-${i}`}
                            >
                                <div className="text-[#e8c86a] text-4xl mb-4"><i className="fa-solid fa-quote-right" /></div>
                                <p className="text-white/85 leading-relaxed mb-6 text-sm">{t.text}</p>
                                <div className="pt-4 border-t border-[rgba(201,169,97,0.2)]">
                                    <div className="font-display text-white text-lg">{t.name}</div>
                                    <div className="text-[#c9a961] text-xs mt-1">{t.role}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="marble-bg py-24">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <div className="relative rounded-3xl p-10 md:p-16 overflow-hidden" style={{ background: "linear-gradient(135deg, #14120a 0%, #0a0a0a 100%)" }}>
                        <div className="absolute inset-0 opacity-40">
                            <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #e8c86a, transparent)" }} />
                            <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #c9a961, transparent)" }} />
                        </div>
                        <div className="relative">
                            <h2 className="section-title text-white mb-5">
                                جاهزون لإعادة <span className="gold-text">بريق</span> رخامك؟
                            </h2>
                            <p className="text-white/70 mb-8 max-w-xl mx-auto">معاينة مجانية وعرض سعر خلال ساعات - تواصل معنا الآن</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="gold-btn" data-testid="cta-whatsapp">
                                    <i className="fa-brands fa-whatsapp ml-2" />واتساب فوري
                                </a>
                                <Link to="/contact" className="outline-btn" data-testid="cta-contact">
                                    نموذج التواصل <i className="fa-solid fa-arrow-left mr-2" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
